// "Check the warehouse for new items" — the one click every stock picker offers
// when the item the customer wants is missing because it was added in ArgoWMS
// after Nexus's last scheduled pull.
//
// The click is the ONLY trigger. Nothing here runs on mount, on open, on a
// keystroke or on a timer: each refresh asks Nexus to pull from the warehouse,
// and that traffic is rationed by design. Nexus rate-limits it per Company and
// falls back to the plain search, so a refresh never errors where a search would
// not.
//
// The refreshed answer is written straight into the caller's query cache, so the
// list on screen IS the refreshed one, never a cached page from before the pull.
// Every other stock list is then marked stale: Nexus reads the item search and
// the fulfillment SKU list from the same item master, so a pull for one is new
// data for both, and a mounted sibling picker refetches at once.

import { hashKey, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useRef, useState } from 'react';

// The query-key roots of every list Nexus reads from the Company's item master.
const STOCK_QUERY_ROOTS: readonly unknown[] = ['company-items', 'company-fulfillment-status'];

export function useWarehouseRefresh<T>(opts: {
  queryKey: QueryKey;
  fetch: () => Promise<T>;
}): { refreshing: boolean; refresh: () => void } {
  const queryClient = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  // A second click while one is running would be a second pull for the same
  // answer; the ref catches it before the busy state has re-rendered.
  const inFlight = useRef(false);

  function refresh(): void {
    if (inFlight.current) return;
    inFlight.current = true;
    setRefreshing(true);
    const key = opts.queryKey;
    const keyHash = hashKey(key);
    void (async () => {
      try {
        const fresh = await opts.fetch();
        // A plain read of this key started before the pull would otherwise land
        // after it and put the pre-pull list back on screen.
        await queryClient.cancelQueries({ queryKey: key, exact: true });
        queryClient.setQueryData(key, fresh);
        await queryClient.invalidateQueries({
          predicate: (q) => STOCK_QUERY_ROOTS.includes(q.queryKey[0]) && q.queryHash !== keyHash,
        });
      } catch {
        // The list on screen stays as it was and the action can be clicked again;
        // the picker is a convenience, never a gate.
      } finally {
        inFlight.current = false;
        setRefreshing(false);
      }
    })();
  }

  return { refreshing, refresh };
}
